import React, { useEffect } from 'react';
import { Button, Image } from 'react-bootstrap';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { authAPI, getDeviceInfo } from '../../helpers/api_service';
import { OAUTH_CLIENTS, APP_URLS } from '../../helpers/url_helper';
import sessionManager from '../../helpers/session_manager';
import { clearAllOAuthData, forceMicrosoftReAuth } from '../../helpers/oauth_logout';
import { getFCMToken, isNotificationSupported } from '../../firebase';
import MicrosoftIcon from '../../assets/icmicrosoft.svg';

// MSAL Configuration
const msalConfig = {
  auth: {
    clientId: OAUTH_CLIENTS.MICROSOFT,
    authority: "https://login.microsoftonline.com/common",
    //redirectUri: "http://localhost:3000/login",
    redirectUri: "https://devfrontend.complimate.com.au/login",
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    allowRedirectInIframe: true,
    iframeHashTimeout: 6000,
    loadFrameTimeout: 0,
    asyncPopups: true,
    popupHashTimeout: 6000,
    popupWindowAttributes: {
      scrollbars: true,
      resizable: true,
      width: 500,
      height: 600
    }
  }
};

// MSAL Instance
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
msalInstance.initialize().then(() => {
  console.log('MSAL initialized successfully');
}).catch(error => {
  console.error('MSAL initialization failed:', error);
});

const MicrosoftLogin = ({ onError, onSuccess, isLoading, setIsLoading, navigate, onDeviceConflict }) => {
  // Function to proceed with login after device conflict is resolved
  const proceedWithMicrosoftLogin = (loginData) => {
    sessionManager.startSession(loginData.token);

    localStorage.setItem('userData', JSON.stringify(loginData));
    localStorage.setItem('userFullName', loginData.full_name || '');
    localStorage.setItem('userProfilePic', loginData.profile_pic || '');

    // Check if user is new and redirect accordingly
    if (loginData.is_new_user === true) {
      console.log('=== NEW USER DETECTED ===');
      console.log('Redirecting to complete profile page');
      navigate('/complete-profile');
    } else {
      console.log('=== EXISTING USER ===');
      // Check subscription package
      if (!loginData.subscription_package || loginData.subscription_package.status === 'expired') {
        console.log('=== NO SUBSCRIPTION OR EXPIRED ===');
        console.log('Redirecting to newSubscription page');
        navigate('/newSubscription');
      } else {
        console.log('=== ACTIVE SUBSCRIPTION ===');
        console.log('Redirecting to dashboard');
        navigate(APP_URLS.DASHBOARD);
      }
    }
  };

  // Handle Microsoft redirect flow
  useEffect(() => {
    const handleMicrosoftRedirect = async () => {
      try {
        console.log('=== CHECKING FOR MICROSOFT REDIRECT ===');
        console.log('Current URL:', window.location.href);
        console.log('Hash:', window.location.hash);
        console.log('Search params:', window.location.search);
        
        // Check if we have a Microsoft auth code in the URL
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        
        const code = urlParams.get('code') || hashParams.get('code');
        const error = urlParams.get('error') || hashParams.get('error');
        const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');
        
        console.log('=== MICROSOFT REDIRECT PARAMS ===');
        console.log('Code:', code);
        console.log('Error:', error);
        console.log('Error description:', errorDescription);
        
        if (error) {
          console.error('Microsoft redirect error:', error, errorDescription);
          onError(`Microsoft authentication failed: ${errorDescription || error}`);
          return;
        }
        
        if (code) {
          console.log('=== PROCESSING MICROSOFT AUTH CODE ===');
          setIsLoading(true);
          
          try {
            // Handle the redirect response
            const response = await msalInstance.handleRedirectPromise();
            console.log('=== MICROSOFT REDIRECT RESPONSE ===');
            console.log('Response:', response);
            
            if (response) {
              console.log('=== MICROSOFT AUTHENTICATION SUCCESS ===');
              console.log('Account:', response.account);
              console.log('Access token:', response.accessToken);
              
              // Process the successful authentication
              await handleMicrosoftSuccess(response.account);
            } else {
              console.log('No response from handleRedirectPromise, trying to extract from hash');
              
              // Try to extract account info from client_info in hash
              const clientInfo = hashParams.get('client_info');
              if (clientInfo) {
                try {
                  const decodedClientInfo = JSON.parse(atob(clientInfo));
                  console.log('=== DECODED CLIENT INFO ===');
                  console.log('Client info:', decodedClientInfo);
                  
                  // Create a mock account object from client info
                  const mockAccount = {
                    localAccountId: decodedClientInfo.uid || decodedClientInfo.sub,
                    username: decodedClientInfo.preferred_username,
                    name: decodedClientInfo.name,
                    environment: 'login.microsoftonline.com',
                    tenantId: decodedClientInfo.tid,
                    homeAccountId: decodedClientInfo.home_oid
                  };
                  
                  console.log('=== CREATED MOCK ACCOUNT ===');
                  console.log('Mock account:', mockAccount);
                  
                  // Process the authentication with mock account
                  await handleMicrosoftSuccess(mockAccount);
                } catch (decodeError) {
                  console.error('Error decoding client info:', decodeError);
                  onError('Failed to decode Microsoft authentication response');
                }
              } else {
                console.log('No client_info found in hash');
                onError('Microsoft authentication response not found');
              }
            }
          } catch (redirectError) {
            console.error('=== MICROSOFT REDIRECT ERROR ===');
            console.error('Redirect error:', redirectError);
            onError('Failed to process Microsoft authentication response');
          } finally {
            setIsLoading(false);
          }
        }
      } catch (err) {
        console.error('Error handling Microsoft redirect:', err);
      }
    };

    handleMicrosoftRedirect();
  }, [onError, setIsLoading]);
  // const handleMicrosoftSuccess = async (account) => {
  //   setIsLoading(true);
  //   onError('');

  //   try {
  //     console.log('=== HANDLING MICROSOFT SUCCESS ===');
  //     console.log('Account received:', account);
  //     console.log('Account type:', typeof account);
  //     console.log('Account keys:', account ? Object.keys(account) : 'No account');
      
  //     if (account) {
  //       console.log('=== MICROSOFT ACCOUNT DETAILS ===');
  //       console.log('Account ID:', account.localAccountId);
  //       console.log('Account username:', account.username);
  //       console.log('Account name:', account.name);
  //       console.log('Account environment:', account.environment);
  //       console.log('Account tenant ID:', account.tenantId);
  //       console.log('Account home account ID:', account.homeAccountId);
  //     }
      
  //     // Get access token for Microsoft Graph
  //     const tokenRequest = {
  //       scopes: ['User.Read']
  //     };
      
  //     const tokenResponse = await msalInstance.acquireTokenSilent({
  //       ...tokenRequest,
  //       account: account
  //     });
      
  //     console.log('=== MICROSOFT TOKEN RESPONSE ===');
  //     console.log('Token response object:', tokenResponse);
  //     console.log('Token response keys:', Object.keys(tokenResponse));
  //     console.log('Access token exists:', !!tokenResponse.accessToken);
  //     console.log('Access token length:', tokenResponse.accessToken ? tokenResponse.accessToken.length : 0);
  //     console.log('Token type:', tokenResponse.tokenType);
  //     console.log('Scopes:', tokenResponse.scopes);
  //     console.log('Expires on:', tokenResponse.expiresOn);
  //     console.log('Account in token response:', tokenResponse.account);
      
  //     // Fetch user profile from Microsoft Graph
  //     const graphResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
  //       headers: {
  //         'Authorization': `Bearer ${tokenResponse.accessToken}`,
  //         'Content-Type': 'application/json'
  //       }
  //     });
      
  //     if (graphResponse.ok) {
  //       const profileData = await graphResponse.json();
  //       console.log('=== MICROSOFT GRAPH API RESPONSE ===');
  //       console.log('Graph response status:', graphResponse.status);
  //       console.log('Graph response headers:', Object.fromEntries(graphResponse.headers.entries()));
  //       console.log('Raw profile data:', profileData);
  //       console.log('Profile data keys:', Object.keys(profileData));
  //       console.log('User ID:', profileData.id);
  //       console.log('Display name:', profileData.displayName);
  //       console.log('Email:', profileData.mail);
  //       console.log('User principal name:', profileData.userPrincipalName);
  //       console.log('Given name:', profileData.givenName);
  //       console.log('Surname:', profileData.surname);
  //       console.log('Job title:', profileData.jobTitle);
  //       console.log('Department:', profileData.department);
  //       console.log('Office location:', profileData.officeLocation);
  //       console.log('Business phones:', profileData.businessPhones);
  //       console.log('Mobile phone:', profileData.mobilePhone);
  //       console.log('Preferred language:', profileData.preferredLanguage);
  //       console.log('Mail nickname:', profileData.mailNickname);
  //       console.log('Account enabled:', profileData.accountEnabled);
        
  //       // Extract user details and format for API
  //       const userDetails = {
  //         provider: "microsoft",
  //         provider_id: profileData.id,
  //         email: profileData.mail || profileData.userPrincipalName,
  //         name: profileData.displayName,
  //         picture: profileData.photo ? `https://graph.microsoft.com/v1.0/me/photo/$value` : '',
  //         username: profileData.mail || profileData.userPrincipalName
  //       };
        
  //       console.log('=== EXTRACTED USER DETAILS ===');
  //       console.log('User details for backend:', userDetails);
        
  //       // Store in localStorage for reference
  //       localStorage.setItem('microsoftUserDetails', JSON.stringify(userDetails));
  //       console.log('=== STORED IN LOCALSTORAGE ===');
  //       console.log('Microsoft user details stored:', userDetails);
        
  //       // Call your backend API with the user details
  //       const data = await authAPI.socialLogin(userDetails);

  //       console.log('=== BACKEND RESPONSE ===');
  //       console.log('Backend response:', data);

  //       if (data.status === 'success') {
  //         console.log('=== AUTHENTICATION SUCCESS ===');
  //         console.log('Token received:', data.data.token);
  //         console.log('User data from backend:', data.data);
          
  //         // Start session using session manager
  //         sessionManager.startSession(data.data.token);
          
  //         // Save additional user data for topbar
  //         localStorage.setItem('userData', JSON.stringify(data.data));
  //         localStorage.setItem('userFullName', data.data.full_name || '');
  //         localStorage.setItem('userProfilePic', data.data.profile_pic || '');
          
  //         console.log('=== SESSION CREATED ===');
  //         console.log('Session started successfully');
  //         console.log('User data saved to localStorage:', {
  //           userData: data.data,
  //           userFullName: data.data.full_name || '',
  //           userProfilePic: data.data.profile_pic || ''
  //         });
          
  //         // Navigate to dashboard
  //         navigate('/dashboard');
  //       } else {
  //         console.log('=== AUTHENTICATION FAILED ===');
  //         console.log('Backend returned error:', data.message);
  //         onError(data.message || 'Microsoft authentication failed. Please try again.');
  //       }
        
  //     } else {
  //       console.error('Failed to fetch Microsoft profile:', graphResponse.status, graphResponse.statusText);
  //       throw new Error('Failed to fetch user profile from Microsoft');
  //     }
      
  //   } catch (err) {
  //     console.error('=== MICROSOFT LOGIN ERROR ===');
  //     console.error('Network or API error:', err);
      
  //     if (err instanceof InteractionRequiredAuthError) {
  //       onError('Microsoft authentication was cancelled. Please try again.');
  //     } else {
  //       onError('Network error. Please check your connection and try again.');
  //     }
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleMicrosoftSuccess = async (account) => {
    setIsLoading(true);
    onError('');

    // Force Microsoft re-authentication by clearing all MSAL state
    forceMicrosoftReAuth();
  
    try {
      console.log('=== HANDLING MICROSOFT SUCCESS ===');
      console.log('Account received:', account);
  
      // Get access token for Microsoft Graph
      const tokenRequest = {
        scopes: ['User.Read']
      };
  
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: account
      });
  
      console.log('=== MICROSOFT TOKEN RESPONSE ===', tokenResponse);
  
      // Fetch user profile from Microsoft Graph
      const graphResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          'Authorization': `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
  
      if (!graphResponse.ok) {
        throw new Error('Failed to fetch user profile from Microsoft');
      }
  
      const profileData = await graphResponse.json();
  
      // === FETCH PROFILE PICTURE ===
      let profilePictureUrl = '';
      try {
        const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: {
            'Authorization': `Bearer ${tokenResponse.accessToken}`
          }
        });
  
        if (photoResponse.ok) {
          const blob = await photoResponse.blob();
          profilePictureUrl = URL.createObjectURL(blob);
          console.log('Profile picture fetched successfully');
        } else {
          console.warn('No profile picture found or failed to fetch it. Status:', photoResponse.status);
        }
      } catch (photoError) {
        console.error('Error fetching profile picture:', photoError);
      }
  
      // Extract user details and format for backend API
      const userDetails = {
        provider: "microsoft",
        provider_id: profileData.id,
        email: profileData.mail || profileData.userPrincipalName,
        name: profileData.displayName,
        picture: profilePictureUrl,
        username: profileData.mail || profileData.userPrincipalName
      };
  
      console.log('Extracted user details:', userDetails);

      // Helper function to get browser name from device info
      const getBrowserName = () => {
        const deviceInfo = JSON.parse(getDeviceInfo());
        return deviceInfo.browser_name || 'Unknown';
      };

      // Add browser_name and device_type to user details
      // Get FCM token if notifications are supported
      let fcmToken = null;
      if (isNotificationSupported()) {
        try {
          fcmToken = await getFCMToken();
        } catch (fcmError) {
          console.warn('Failed to get FCM token for Microsoft login:', fcmError);
          // Continue with login even if FCM token fails
        }
      }

      const userDetailsWithDeviceInfo = {
        ...userDetails,
        browser_name: getBrowserName(),
        device_type: "web",
        fcm_token: fcmToken
      };

      // Store locally (including access token for logout)
      const userDetailsWithToken = {
        ...userDetailsWithDeviceInfo,
        access_token: tokenResponse.accessToken
      };
      localStorage.setItem('microsoftUserDetails', JSON.stringify(userDetailsWithToken));

      // Authenticate with backend
      const data = await authAPI.socialLogin(userDetailsWithDeviceInfo);
  
            if (data.status === 'success') {
        console.log('=== AUTHENTICATION SUCCESS ===');
        console.log('Token received:', data.data.token);
        console.log('User data from backend:', data.data);
        console.log('Is new user:', data.data.is_new_user);
        
        // Check for device limit reached
        if (data.data.device_limit_reached === true) {
          // Show device conflict popup
          if (onDeviceConflict) {
            onDeviceConflict(data.data.active_device_sessions, data.data);
          }
          return;
        }
        
        // No device conflict, proceed with normal login
        proceedWithMicrosoftLogin(data.data);
      } else {
        onError(data.message || 'Microsoft authentication failed. Please try again.');
      }
  
    } catch (err) {
      console.error('Microsoft login error:', err);
  
      if (err instanceof InteractionRequiredAuthError) {
        onError('Microsoft authentication was cancelled. Please try again.');
      } else {
        onError('Network error. Please check your connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleMicrosoftError = (error) => {
    console.error('=== MICROSOFT AUTHENTICATION ERROR ===');
    console.error('Error details:', error);
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    console.error('Error code:', error.errorCode);
    console.error('Error stack:', error.stack);
    
    if (error.name === 'BrowserAuthError') {
      onError('Microsoft authentication was cancelled or failed. Please try again.');
    } else if (error.name === 'InteractionRequiredAuthError') {
      onError('Microsoft authentication requires interaction. Please try again.');
    } else if (error.errorCode === 'popup_window_error') {
      onError('Popup blocked. Please allow popups for this site and try again.');
    } else {
      onError(`Microsoft authentication failed: ${error.message || 'Unknown error'}`);
    }
  };

  const handleMicrosoftButtonClick = async () => {
    // Force Microsoft re-authentication by clearing all MSAL state
    forceMicrosoftReAuth();
    
    try {
      console.log('=== STARTING MICROSOFT LOGIN ===');
      console.log('MSAL Instance:', msalInstance);
      console.log('MSAL Config:', msalConfig);
      
      const loginRequest = {
        scopes: ['User.Read']
      };
      
      console.log('Login request:', loginRequest);
      console.log('Redirecting to Microsoft login...');
      
      // Use redirect flow since popup requires SPA configuration
      await msalInstance.loginRedirect(loginRequest);
      // The redirect will be handled in useEffect
      
    } catch (error) {
      console.error('=== MICROSOFT BUTTON CLICK ERROR ===');
      console.error('Full error object:', error);
      handleMicrosoftError(error);
    }
  };

  return (
    <Button 
      className="login-social-btn w-50 d-flex align-items-center justify-content-center"
      onClick={handleMicrosoftButtonClick}
      disabled={isLoading}
    >
      <Image src={MicrosoftIcon} alt="Microsoft Icon" width={24} height={24} />
      <span className="ms-2">Log in with Microsoft</span>
    </Button>
  );
};

export default MicrosoftLogin; 